// Every prompt the app sends, in one place — pure functions of explicit
// inputs, so the eval harness can exercise and iterate on them directly.

import type { Constraint, DecisionNode, Option } from "./state";

/** The house style the doc synthesiser writes in — fixed config, not runtime state. */
export const STYLE = "concise, senior-engineer PRD; tradeoff-first; bullet-heavy; assumes reader knows the stack";

export interface PromptCtx {
  idea: string;
  grounding: string;      // pre-formatted "k=v, k=v" line (or a placeholder)
  facts: string[];
  constraints: Constraint[];
}

export function navPrompt(ctx: PromptCtx, { pathText, isRoot }: { pathText: string; isRoot: boolean }): string {
  const facts = ctx.facts.length ? ctx.facts.map((f) => `- ${f}`).join("\n") : "(none)";
  // Cross-branch coherence is compiled IN (§11): the navigator can't see the
  // rest of the graph, so detected tensions are pinned into every call.
  const chips = ctx.constraints.length
    ? `\nCROSS-BRANCH CONSTRAINTS (pinned — your options must respect these):\n${ctx.constraints.map((c) => `- ${c.text}`).join("\n")}`
    : "";
  const root = `You are the Navigator in a design-exploration tool for senior engineers.
Given a design idea and the decisions made so far, propose the SINGLE most important NEXT decision to make, with 2-4 concrete, mutually-exclusive options. Each option is one line with its key engineering tradeoff. Be specific and technical; no fluff.

Return ONLY valid minified JSON, no markdown, no commentary:
{"branch":"<2-3 word area, e.g. data model / ingestion / query path / cost / rollout / auth>","question":"<the decision, <=130 chars, phrased as a question>","options":[{"label":"<concrete choice, <=64 chars>","tradeoff":"<the crux tradeoff, <=120 chars>"}],"recommend":<0-based index of the option you'd pick as the sensible default for THIS system given the grounding>,"stakes":"low"|"med"|"high","confidence":<0..1 how well-grounded this decision now is>}
- stakes = how consequential this decision is: "low" = safe to auto-accept the default and move on; "high" = load-bearing, the user should really weigh it.`;

  const rootExtra = isRoot
    ? `\nThis is the FIRST decision. Set question to "Which part of the design should we tackle first?" and make each option one MAJOR branch/system this design needs (data model, ingestion, query path, cost, rollout, auth, etc.); the tradeoff = why that area is load-bearing or what it constrains. Give 4-5 options.`
    : "";

  return `${root}${rootExtra}

IDEA: ${ctx.idea}
SIMULATED PRODUCTION GROUNDING: ${ctx.grounding}
USER-INJECTED FACTS (must respect):
${facts}${chips}
DECISIONS SO FAR:
${pathText}

Now emit the JSON for the next decision.`;
}

export function groundingPrompt(idea: string): string {
  return `A senior engineer is designing: "${idea}".
Invent 5 plausible PRODUCTION grounding scalars that would inform this design (as if pulled from CloudWatch / the DB / billing). Pre-reduced to scalars only.
Return ONLY minified JSON: {"metrics":[{"k":"<short key e.g. peak RPS>","v":"<value e.g. 4.2k>"}]}`;
}

export function constraintsPrompt(idea: string, groupedDecisions: string): string {
  return `You are the Context Compiler in a design tool. Decisions so far, grouped by branch of one system design ("${idea}"):
${groupedDecisions}

Identify CROSS-BRANCH constraints: where a decision in one branch constrains, or conflicts with, a decision (or likely future decision) in ANOTHER branch. Only real load-bearing tensions — 0 to 3. If none, return an empty list.
Return ONLY minified JSON: {"constraints":[{"text":"<the tension and what it implies, <=120 chars>","branches":["<branch>","<branch>"]}]}`;
}

export function docRoutePrompt(
  ctx: PromptCtx,
  node: DecisionNode,
  chosen: Option,
  currentDoc: string,
  sections: readonly string[],
  sectionGuide?: Record<string, string>,
): string {
  const sectionList = sectionGuide
    ? sections.map((s) => `- ${s}: ${sectionGuide[s] || ""}`).join("\n")
    : sections.join(" | ");
  return `You maintain a design doc (Marqo template) for: "${ctx.idea}".
GROUNDING: ${ctx.grounding}

A decision was just made:
[${node.branch}] ${node.question}
→ CHOSE: ${chosen.label} — ${chosen.tradeoff} (stakes: ${node.stakes})

CURRENT DOC CONTENT (sections not listed are still empty):
${currentDoc}

SECTIONS (name: what belongs there — respect this):
${sectionList}

Route this decision into the 1-3 sections it genuinely affects (e.g. a storage
choice may touch Data Storage / Modeling AND Non-Functional Requirements AND
Out Of Scope). For each, return the section's UPDATED FULL TEXT: merge the new
decision into the existing text (rewrite, don't append), keep it terse (1-5
sentences or bullets), use FR-n / NFR-n ids in the requirements sections, cite
grounding scalars where relevant. Content must fit the section's purpose.
Return ONLY minified JSON: {"updates":[{"section":"<exact section name>","content":"<updated full text>"}]}`;
}

export function docCompletePrompt(
  ctx: PromptCtx,
  traceText: string,
  filledSummary: string,
  emptySections: string[],
  sectionGuide?: Record<string, string>,
): string {
  const sectionList = emptySections
    .map((s) => `- ${s}: ${sectionGuide?.[s] || ""}`)
    .join("\n");
  return `You maintain a design doc (Marqo template) for: "${ctx.idea}".
GROUNDING: ${ctx.grounding}
USER-INJECTED FACTS: ${ctx.facts.join("; ") || "none"}

FULL DECISION TRACE:
${traceText}

ALREADY-WRITTEN SECTIONS (for context — do NOT rewrite these):
${filledSummary || "(none)"}

These template sections are still EMPTY (name: what belongs there — respect this):
${sectionList}

For EACH empty section, derive content from the decision trace: terse, 1-5
sentences or bullets, senior-engineer register, cite grounding scalars where
relevant, use FR-n/NFR-n ids in requirements sections. Content must fit the
section's stated purpose — do not repeat another section's content. If the
trace genuinely gives nothing for a section, use exactly "N/A".
Return ONLY minified JSON: {"updates":[{"section":"<exact section name>","content":"<text or N/A>"}]}`;
}

export function judgePrompt(ctx: PromptCtx, node: DecisionNode, chosen: Option, priorPath: string): string {
  return `You are a senior staff engineer doing a design review. Judge ONE decision for soundness under full reasoning — not whether it's popular, but whether it holds up for THIS system given the numbers.

IDEA: ${ctx.idea}
GROUNDING (real scalars): ${ctx.grounding}
INJECTED FACTS: ${ctx.facts.join("; ") || "none"}
EARLIER DECISIONS ON THIS PATH:
${priorPath}

DECISION UNDER REVIEW: ${node.question}
OPTIONS THAT WERE ON THE TABLE:
${node.options.map((o, i) => `${i + 1}. ${o.label} — ${o.tradeoff}`).join("\n")}
CHOSEN: ${chosen.label}

Return ONLY minified JSON:
{"verdict":"sound"|"weak"|"risky","note":"<=140 chars: the crux — why it holds or where it breaks","better":"<a stronger option label consistent with the intent, or empty string if the choice stands>"}
- sound: defensible given the grounding.
- weak: works but a listed/obvious alternative is clearly better here.
- risky: likely to break at these numbers (cite which scalar).`;
}

export function decomposePrompt(doc: string): string {
  return `You are the Decomposer in a design tool. Below is an EXISTING design document. Recover its decision trace: the key engineering decisions it embodies, the 2-4 options that were (implicitly) on the table for each, and which option the doc actually chose.

Return ONLY minified JSON, no markdown:
{"idea":"<one-sentence essence of what is being designed>","decisions":[{"branch":"<2-3 word area>","question":"<the decision as a question, <=130 chars>","options":[{"label":"<concrete choice, <=64 chars>","tradeoff":"<crux tradeoff, <=120 chars>"}],"chosen":<0-based index of the option the doc took>,"stakes":"low"|"med"|"high"}]}
4-8 decisions in the doc's own order. Options must be real alternatives, not strawmen. Keep every field terse.

DOC:
${doc}`;
}

export function comparePrompt(ctx: PromptCtx, question: string, futureA: string, futureB: string): string {
  return `You are a senior staff engineer. For the design "${ctx.idea}" (grounding: ${ctx.grounding}), the decision "${question}" was explored down BOTH of two options, taking sensible defaults:

FUTURE A: ${futureA}
FUTURE B: ${futureB}

Return ONLY minified JSON:
{"pick":"A"|"B","why":"<=140 chars, the deciding factor for THIS system>","aRisk":"<=100 chars, future A's biggest risk>","bRisk":"<=100 chars, future B's biggest risk>"}`;
}

export function synthesisPrompt(
  ctx: PromptCtx,
  traceText: string,
  reviewFindings: string,
  projectionInstr: string,
): string {
  return `You are the Doc Synthesiser: a strong agent that renders a design-exploration trace into a finished design doc.

STYLE: ${STYLE}
IDEA: ${ctx.idea}
SIMULATED PRODUCTION GROUNDING: ${ctx.grounding}
USER-INJECTED FACTS: ${ctx.facts.join("; ") || "none"}
${ctx.constraints.length ? `CROSS-BRANCH CONSTRAINTS (detected by the compiler — address them in the doc):\n${ctx.constraints.map((c) => `- ${c.text}`).join("\n")}` : ""}

DECISION TRACE (each is a decision the engineer navigated, with the tradeoff taken):
${traceText}
${reviewFindings ? `\nREPLAY REVIEW FINDINGS (a stronger agent re-judged these decisions — fold them into Open Questions, don't hide them):\n${reviewFindings}\n` : ""}
Write a crisp design doc in Markdown from this trace.
${projectionInstr}

Be terse and senior; no preamble or restated prompt. Every claim should trace to a decision or a scalar, not invented specifics.`;
}
